/**
 * Coherent-IA Task 2: the raga-scale player that hydrates a `[data-raga-scale]`
 * container on a static `/ragas/<slug>/` content page — tap a swara to hear
 * it, "Play scale ▶" a whole row — reusing the SAME `TonePlayer` (`tone.ts`)
 * the in-app raga rail (`src/ui/raga-rail.ts`) already plays chips through.
 * No duplicate audio logic here: this module only reads the per-swara `cents`
 * that `scripts/gen-content/raga-page.ts` already computed at generation time
 * (via `playableScale`, the exact function the rail itself uses) and baked
 * into `data-cents`/`data-oscillatory` attributes on the static markup — so
 * this widget needs no seed fetch and no re-derivation of raga theory.
 *
 * DOM-only + a `TonePlayer` — no framework, no state beyond the locally
 * selected Sa. Kept deliberately small: `content-widgets.ts` (the entry that
 * imports this) is the whole progressive-enhancement bundle content pages
 * opt into, and it ships to every visitor of a raga page.
 */
import { noteToFrequency } from '@swarasense/shared';
import type { TonePlayer } from '../audio/tone.js';
import { loadSa } from '../api/profile.js';
import { mountSaControl } from './sa-control.js';

/** Connected-Swarasense (W2): the raga-page Sa is the SHARED shruti
 * (`profile.sa`) — the same Sa as the tanpura page and the practice tool, via
 * the full pitch/octave `sa-control`. This replaced a static three-preset
 * C3/C4/C5 select that never read your profile (the "why is it only C3/4/5?"
 * complaint). `saHzFromSa` converts the chosen note to a reference frequency. */
const saHzFromSa = (sa: { pitchClass: string; octave: number }): number =>
  noteToFrequency(`${sa.pitchClass}${sa.octave}`);

/** A single note's approximate on-screen "lit while it sounds" duration —
 * mirrors `raga-rail.ts`'s own `CHIP_LIT_MS` (and, in turn, `tone.ts`'s
 * private `DEFAULT_DURATION_MS`). Visual-only; the audio itself doesn't
 * depend on this constant. */
const CHIP_LIT_MS = 700;

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/** Briefly marks `chip` as "sounding" — the tap feedback for a single swara. */
function litChip(chip: HTMLElement): void {
  chip.classList.add('is-playing');
  window.setTimeout(() => chip.classList.remove('is-playing'), Math.max(0, CHIP_LIT_MS - 40));
}

/** "Play scale ▶": steps the highlight across `chips` in time with the
 * phrase. Under `prefers-reduced-motion`, a single static highlight over the
 * whole row for the phrase's total duration instead of an animated sweep —
 * same degrade `raga-rail.ts`'s `sweepChips` uses. */
function sweepChips(chips: readonly HTMLElement[]): void {
  if (reducedMotion()) {
    for (const chip of chips) chip.classList.add('is-playing');
    window.setTimeout(() => {
      for (const chip of chips) chip.classList.remove('is-playing');
    }, chips.length * CHIP_LIT_MS);
    return;
  }
  chips.forEach((chip, i) => {
    window.setTimeout(() => litChip(chip), i * CHIP_LIT_MS);
  });
}

/** Reads a chip/button's `data-cents` as a finite number, or `undefined` if
 * missing/malformed (defensive only — `raga-page.ts` always emits a valid
 * numeric string, but a widget hydrating hand-authored/future markup
 * shouldn't crash on a bad attribute). */
function centsOf(el: HTMLElement): number | undefined {
  const raw = el.dataset.cents;
  if (raw === undefined) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Hydrates one `[data-raga-scale]` container: wires every `.scale-swara`
 * chip's click (tap → hear that swara) and every `.raga-scale-play` row
 * button's click (hear the whole row as a phrase), plus the optional Sa
 * selector. `tonePlayer` is shared across every widget `content-widgets.ts`
 * hydrates on the page (there's normally exactly one per raga page), so a
 * tap on one widget correctly cuts off playback from another.
 */
export function hydrateRagaScale(container: HTMLElement, tonePlayer: TonePlayer): void {
  let saHz = saHzFromSa(loadSa());

  const chips = Array.from(container.querySelectorAll<HTMLButtonElement>('.scale-swara'));
  for (const chip of chips) {
    chip.addEventListener('click', () => {
      const cents = centsOf(chip);
      if (cents === undefined) return;
      try {
        tonePlayer.unlock(); // SYNC, first thing, still inside the click gesture
        tonePlayer.stop(); // clean restart on a fast re-tap (no layered tones)
        litChip(chip);
        void tonePlayer.playSwara(saHz, cents);
      } catch (err) {
        // Fail soft — a blocked/unavailable AudioContext should never break
        // the rest of the page (playSwara/stop/unlock are already fail-soft
        // themselves; this is a defensive backstop for anything else, e.g. a
        // malformed DOM, that might throw synchronously here).
        console.warn('raga-scale-widget: could not play swara', err);
      }
    });
  }

  const rows = Array.from(container.querySelectorAll<HTMLElement>('.raga-scale-row'));
  for (const row of rows) {
    const playButton = row.querySelector<HTMLButtonElement>('.raga-scale-play');
    const rowChips = Array.from(row.querySelectorAll<HTMLButtonElement>('.scale-swara'));
    playButton?.addEventListener('click', () => {
      const notes = rowChips
        .map((chip) => centsOf(chip))
        .filter((c): c is number => c !== undefined)
        .map((cents) => ({ cents }));
      if (notes.length === 0) return;
      try {
        tonePlayer.unlock();
        tonePlayer.stop();
        sweepChips(rowChips);
        void tonePlayer.playPhrase(saHz, notes);
      } catch (err) {
        console.warn('raga-scale-widget: could not play scale', err);
      }
    });
  }

  // Mount the shared Sa control (full pitch/octave, bound to profile.sa). It
  // seeds from `loadSa()`, persists changes site-wide, and reflects a Sa set on
  // another page/tab — keeping `saHz` (what the tap/scale playback uses) in sync.
  const saSlot = container.querySelector<HTMLElement>('[data-sa-control]');
  if (saSlot) {
    mountSaControl(saSlot, {
      onChange: (sa) => {
        saHz = saHzFromSa(sa);
      },
    });
  }
}
